import React, { useState, useRef } from 'react';
import {
    Business,
    Add,
    Edit,
    Delete,
    Search,
    FilterList,
    Group,
    Store,
    Restaurant,
    Build,
    ShoppingCart,
    Apartment,
    CheckCircle,
    MoreVert,
    Visibility,
    Settings,
    Phone,
    CameraAlt,
    Login,
    ArrowForward
} from '@mui/icons-material';
import AdminSidebar from "../../components/admin/AdminSidebar";
import DashboardHeader from "../../components/admin/DashboardHeader";

const OrganizationsPage = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [timeRange, setTimeRange] = useState('today');
    const [currency, setCurrency] = useState('CDF');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedOrganization, setSelectedOrganization] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');
    const [hoveredMenu, setHoveredMenu] = useState(null);

    // Données des organisations
    const [organizations, setOrganizations] = useState([
        {
            id: 1,
            name: 'Boutique Elegance',
            category: 'retail',
            logo: '🏪',
            profilePicture: '',
            description: 'Boutique de vêtements et accessoires de mode',
            phoneNumber: '+243 97 123 4567',
            status: 'active',
            users: 3,
            createdAt: '2024-01-15',
            revenue: 12500,
            role: 'admin'
        },
        {
            id: 2,
            name: 'Restaurant Le Gourmet',
            category: 'restaurant',
            logo: '🍽️',
            profilePicture: '',
            description: 'Restaurant gastronomique français',
            phoneNumber: '+243 89 987 6543',
            status: 'active',
            users: 5,
            createdAt: '2024-01-10',
            revenue: 28500,
            role: 'both'
        },
        {
            id: 3,
            name: 'Tech Solutions SARL',
            category: 'service',
            logo: '🔧',
            profilePicture: '',
            description: 'Services informatiques et consulting',
            phoneNumber: '+243 81 555 1234',
            status: 'inactive',
            users: 2,
            createdAt: '2024-01-05',
            revenue: 8500,
            role: 'admin'
        },
        {
            id: 4,
            name: 'Shop Online RDC',
            category: 'ecommerce',
            logo: '🛒',
            profilePicture: '',
            description: 'Plateforme e-commerce nationale',
            phoneNumber: '+243 99 888 7777',
            status: 'active',
            users: 8,
            createdAt: '2024-01-20',
            revenue: 42000,
            role: 'cashier'
        }
    ]);

    const [newOrganization, setNewOrganization] = useState({
        name: '',
        category: 'retail',
        description: '',
        logo: '🏢',
        profilePicture: '',
        phoneNumber: '+243 '
    });

    // Catégories disponibles
    const categories = [
        { id: 'retail', label: 'Commerce de détail', icon: Store, color: 'from-blue-500 to-cyan-500' },
        { id: 'restaurant', label: 'Restauration', icon: Restaurant, color: 'from-orange-500 to-red-500' },
        { id: 'service', label: 'Service', icon: Build, color: 'from-green-500 to-emerald-500' },
        { id: 'ecommerce', label: 'E-commerce', icon: ShoppingCart, color: 'from-purple-500 to-pink-500' },
        { id: 'other', label: 'Autre', icon: Apartment, color: 'from-gray-500 to-gray-600' }
    ];

    const getCategoryConfig = (categoryId) => {
        return categories.find(cat => cat.id === categoryId) || categories[4];
    };

    const getStatusColor = (status) => {
        return status === 'active'
            ? 'bg-green-100 text-green-800'
            : 'bg-gray-100 text-gray-800';
    };

    const getRoleConfig = (role) => {
        const configs = {
            admin: { label: 'Administrateur', color: 'bg-purple-100 text-purple-800' },
            cashier: { label: 'Caissier', color: 'bg-blue-100 text-blue-800' },
            both: { label: 'Admin & Caissier', color: 'bg-amber-100 text-amber-800' }
        };
        return configs[role] || { label: 'Utilisateur', color: 'bg-gray-100 text-gray-800' };
    };

    // Filtrer les organisations
    const filteredOrganizations = organizations.filter(org => {
        const matchesSearch = org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            org.description.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = filterCategory === 'all' || org.category === filterCategory;
        return matchesSearch && matchesCategory;
    });

    const handleCreateOrganization = () => {
        const organization = {
            id: organizations.length + 1,
            ...newOrganization,
            status: 'active',
            users: 1,
            createdAt: new Date().toISOString().split('T')[0],
            revenue: 0,
            role: 'admin'
        };

        setOrganizations(prev => [organization, ...prev]);
        setShowCreateModal(false);
        setNewOrganization({
            name: '',
            category: 'retail',
            description: '',
            logo: '🏢',
            profilePicture: '',
            phoneNumber: '+243 '
        });
    };

    const handleEditOrganization = () => {
        setOrganizations(prev =>
            prev.map(org =>
                org.id === selectedOrganization.id
                    ? { ...org, ...selectedOrganization }
                    : org
            )
        );
        setShowEditModal(false);
        setSelectedOrganization(null);
    };

    const handleDeleteOrganization = (orgId) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer cette organisation ?')) {
            setOrganizations(prev => prev.filter(org => org.id !== orgId));
        }
    };

    const handleEditClick = (org) => {
        setSelectedOrganization(org);
        setShowEditModal(true);
    };

    const handleInputChange = (field, value) => {
        if (showEditModal && selectedOrganization) {
            setSelectedOrganization(prev => ({ ...prev, [field]: value }));
        } else {
            setNewOrganization(prev => ({ ...prev, [field]: value }));
        }
    };

    const handleConnectToOrganization = (org) => {
        // Logique pour se connecter à l'organisation
        console.log('Connexion à l\'organisation:', org.name);
        alert(`Connexion à ${org.name} réussie !`);
        // Ici vous pouvez rediriger vers le dashboard de l'organisation
    };

    const handleViewDetails = (org) => {
        // Logique pour voir les détails de l'organisation
        console.log('Voir détails de:', org.name);
        // Naviguer vers la page de détails
    };

    const handleSettings = (org) => {
        // Logique pour les paramètres de l'organisation
        console.log('Paramètres de:', org.name);
        // Naviguer vers les paramètres
    };

    const OrganizationCard = ({ org }) => {
        const categoryConfig = getCategoryConfig(org.category);
        const roleConfig = getRoleConfig(org.role);
        const CategoryIcon = categoryConfig.icon;
        const isMenuOpen = hoveredMenu === org.id;

        const getMenuItems = () => {
            const baseItems = [
                {
                    label: 'Se connecter',
                    icon: Login,
                    action: () => handleConnectToOrganization(org),
                    color: 'text-green-600 hover:bg-green-50'
                },
                {
                    label: 'Voir détails',
                    icon: Visibility,
                    action: () => handleViewDetails(org),
                    color: 'text-blue-600 hover:bg-blue-50'
                },
                {
                    label: 'Modifier',
                    icon: Edit,
                    action: () => handleEditClick(org),
                    color: 'text-amber-600 hover:bg-amber-50'
                }
            ];

            // Ajouter les paramètres seulement pour les administrateurs
            if (org.role === 'admin' || org.role === 'both') {
                baseItems.push({
                    label: 'Paramètres',
                    icon: Settings,
                    action: () => handleSettings(org),
                    color: 'text-purple-600 hover:bg-purple-50'
                });
            }

            // Toujours ajouter la suppression à la fin
            baseItems.push({
                label: 'Supprimer',
                icon: Delete,
                action: () => handleDeleteOrganization(org.id),
                color: 'text-red-600 hover:bg-red-50'
            });

            return baseItems;
        };

        return (
            <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:shadow-lg transition-all duration-300 group">
                {/* En-tête de la carte */}
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                        <div className="relative">
                            <div className={`w-12 h-12 bg-gradient-to-r ${categoryConfig.color} rounded-2xl flex items-center justify-center text-white text-lg shadow-md`}>
                                {org.profilePicture ? (
                                    <img
                                        src={org.profilePicture}
                                        alt={org.name}
                                        className="w-full h-full rounded-2xl object-cover"
                                    />
                                ) : (
                                    org.logo
                                )}
                            </div>
                            {org.status === 'active' && (
                                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                            )}
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900 text-lg group-hover:text-amber-600 transition-colors">
                                {org.name}
                            </h3>
                            <div className="flex items-center space-x-2 mt-1">
                                <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(org.status)}`}>
                                    <div className={`w-1.5 h-1.5 rounded-full ${org.status === 'active' ? 'bg-green-500' : 'bg-gray-500'}`}></div>
                                    <span>{org.status === 'active' ? 'Actif' : 'Inactif'}</span>
                                </span>
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${roleConfig.color}`}>
                                    {roleConfig.label}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="relative">
                        <button
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-2xl transition-colors"
                            onMouseEnter={() => setHoveredMenu(org.id)}
                            onMouseLeave={() => setHoveredMenu(null)}
                        >
                            <MoreVert />
                        </button>

                        {/* Menu déroulant dynamique */}
                        {isMenuOpen && (
                            <div
                                className="absolute right-0 top-10 bg-white border border-gray-200 rounded-2xl shadow-lg py-2 w-48 z-50"
                                onMouseEnter={() => setHoveredMenu(org.id)}
                                onMouseLeave={() => setHoveredMenu(null)}
                            >
                                {getMenuItems().map((item, index) => {
                                    const Icon = item.icon;
                                    return (
                                        <button
                                            key={index}
                                            onClick={item.action}
                                            className={`w-full flex items-center space-x-3 px-4 py-2 transition-colors ${item.color}`}
                                        >
                                            <Icon className="text-lg" />
                                            <span>{item.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* Description */}
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {org.description}
                </p>

                {/* Numéro de téléphone */}
                <div className="flex items-center space-x-2 text-gray-500 text-sm mb-3">
                    <Phone className="text-lg" />
                    <span>{org.phoneNumber}</span>
                </div>

                {/* Catégorie */}
                <div className="flex items-center space-x-2 text-gray-500 text-sm mb-4">
                    <CategoryIcon className="text-lg" />
                    <span>{categoryConfig.label}</span>
                </div>

                {/* Statistiques */}
                <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
                    <div className="text-center">
                        <div className="text-lg font-semibold text-gray-900">{org.users}</div>
                        <div className="text-xs text-gray-500">Utilisateurs</div>
                    </div>
                    <div className="text-center">
                        <div className="text-lg font-semibold text-gray-900">
                            {org.revenue.toLocaleString()} {currency}
                        </div>
                        <div className="text-xs text-gray-500">Revenu</div>
                    </div>
                    <div className="text-center">
                        <div className="text-lg font-semibold text-gray-900">
                            {new Date(org.createdAt).toLocaleDateString()}
                        </div>
                        <div className="text-xs text-gray-500">Créé le</div>
                    </div>
                </div>

                {/* Bouton de connexion rapide */}
                <button
                    onClick={() => handleConnectToOrganization(org)}
                    className="w-full mt-4 flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-yellow-500 text-white rounded-2xl font-semibold hover:from-amber-600 hover:to-yellow-600 transition-all group/btn"
                >
                    <span>Se connecter</span>
                    <ArrowForward className="group-hover/btn:translate-x-1 transition-transform" />
                </button>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-white flex">
            <AdminSidebar
                isOpen={sidebarOpen}
                onToggle={() => setSidebarOpen(!sidebarOpen)}
                activeSection="organizations"
            />

            <div className="flex-1 min-h-screen transition-all duration-300 overflow-hidden">
                <div className="bg-gradient-to-br from-gray-50 via-white to-amber-50 min-h-screen p-4 lg:p-6">
                    <div className="relative z-10 space-y-6">
                        <DashboardHeader
                            timeRange={timeRange}
                            onTimeRangeChange={setTimeRange}
                            currency={currency}
                            onCurrencyChange={setCurrency}
                            onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
                        />

                        {/* En-tête de la page */}
                        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6 text-amber-900 shadow-sm">
                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center space-x-4 mb-4">
                                        <div className="p-2 bg-amber-500/10 rounded-xl">
                                            <Business className="text-xl text-amber-600" />
                                        </div>
                                        <div>
                                            <h1 className="text-2xl font-bold text-amber-900">
                                                Gestion des Organisations
                                            </h1>
                                            <p className="text-amber-700">
                                                Gérez toutes vos organisations en un seul endroit
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex space-x-3 mt-6 lg:mt-0">
                                    <button
                                        onClick={() => setShowCreateModal(true)}
                                        className="flex items-center space-x-2 px-6 py-3 bg-white text-amber-600 rounded-2xl hover:bg-opacity-90 transition-all font-semibold shadow-lg"
                                    >
                                        <Add />
                                        <span>Nouvelle Organisation</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Statistiques */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">Total Organisations</p>
                                        <p className="text-2xl font-bold text-gray-900 mt-1">
                                            {organizations.length}
                                        </p>
                                    </div>
                                    <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-2xl flex items-center justify-center">
                                        <Business className="text-white" />
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">Organisations Actives</p>
                                        <p className="text-2xl font-bold text-green-600 mt-1">
                                            {organizations.filter(org => org.status === 'active').length}
                                        </p>
                                    </div>
                                    <div className="w-12 h-12 bg-gradient-to-r from-green-100 to-emerald-100 rounded-2xl flex items-center justify-center">
                                        <CheckCircle className="text-green-600" />
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">Utilisateurs Total</p>
                                        <p className="text-2xl font-bold text-blue-600 mt-1">
                                            {organizations.reduce((sum, org) => sum + org.users, 0)}
                                        </p>
                                    </div>
                                    <div className="w-12 h-12 bg-gradient-to-r from-blue-100 to-cyan-100 rounded-2xl flex items-center justify-center">
                                        <Group className="text-blue-600" />
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">Revenu Total</p>
                                        <p className="text-2xl font-bold text-purple-600 mt-1">
                                            {organizations.reduce((sum, org) => sum + org.revenue, 0).toLocaleString()} {currency}
                                        </p>
                                    </div>
                                    <div className="w-12 h-12 bg-gradient-to-r from-purple-100 to-pink-100 rounded-2xl flex items-center justify-center">
                                        <ShoppingCart className="text-purple-600" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Barre de recherche et filtres */}
                        <div className="bg-white rounded-2xl p-6 border border-gray-200">
                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                                <div className="flex-1 max-w-md">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            placeholder="Rechercher une organisation..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center space-x-4">
                                    <select
                                        value={filterCategory}
                                        onChange={(e) => setFilterCategory(e.target.value)}
                                        className="px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                    >
                                        <option value="all">Toutes les catégories</option>
                                        {categories.map(category => (
                                            <option key={category.id} value={category.id}>
                                                {category.label}
                                            </option>
                                        ))}
                                    </select>

                                    <button className="flex items-center space-x-2 px-4 py-3 border border-gray-300 rounded-2xl hover:bg-gray-50 transition-colors">
                                        <FilterList />
                                        <span>Filtrer</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Liste des organisations */}
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                            {filteredOrganizations.map(org => (
                                <OrganizationCard key={org.id} org={org} />
                            ))}
                        </div>

                        {filteredOrganizations.length === 0 && (
                            <div className="text-center py-12">
                                <div className="w-24 h-24 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <Business className="text-gray-400 text-3xl" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    Aucune organisation trouvée
                                </h3>
                                <p className="text-gray-600 mb-6">
                                    Aucune organisation ne correspond à vos critères de recherche.
                                </p>
                                <button
                                    onClick={() => setShowCreateModal(true)}
                                    className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white px-6 py-3 rounded-2xl font-semibold hover:from-amber-600 hover:to-yellow-600 transition-all"
                                >
                                    Créer une organisation
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal de création */}
            {showCreateModal && (
                <OrganizationModal
                    title="Créer une Organisation"
                    organization={newOrganization}
                    onClose={() => setShowCreateModal(false)}
                    onSave={handleCreateOrganization}
                    onChange={handleInputChange}
                    categories={categories}
                />
            )}

            {/* Modal d'édition */}
            {showEditModal && selectedOrganization && (
                <OrganizationModal
                    title="Modifier l'Organisation"
                    organization={selectedOrganization}
                    onClose={() => setShowEditModal(false)}
                    onSave={handleEditOrganization}
                    onChange={handleInputChange}
                    categories={categories}
                />
            )}
        </div>
    );
};

// Composant Modal réutilisable avec photo de profil
const OrganizationModal = ({ title, organization, onClose, onSave, onChange, categories }) => {
    const fileInputRef = useRef(null);
    const logos = ['🏪', '🍽️', '🔧', '🛒', '🏢', '🏬', '🏨', '💼', '🚀', '🌟'];

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                onChange('profilePicture', e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleLogoClick = () => {
        fileInputRef.current?.click();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h3 className="text-xl font-bold text-gray-900">{title}</h3>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-2xl transition-colors"
                    >
                        <Delete />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {/* Photo de profil */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Photo de profil
                        </label>
                        <div className="flex items-center space-x-6">
                            <div
                                className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-100 to-yellow-100 border-2 border-amber-200 overflow-hidden cursor-pointer group"
                                onClick={handleLogoClick}
                            >
                                {organization.profilePicture ? (
                                    <img
                                        src={organization.profilePicture}
                                        alt="Profile"
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-2xl">
                                        {organization.logo}
                                    </div>
                                )}

                                {/* Overlay au survol */}
                                <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <CameraAlt className="text-white text-lg" />
                                </div>
                            </div>

                            <div className="flex-1">
                                <button
                                    type="button"
                                    onClick={handleLogoClick}
                                    className="px-4 py-2 border border-amber-500 text-amber-600 rounded-2xl hover:bg-amber-50 transition-colors"
                                >
                                    Changer la photo
                                </button>
                                <p className="text-xs text-gray-500 mt-2">
                                    PNG, JPG jusqu'à 2MB. Recommandé: 200x200px
                                </p>
                            </div>

                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                accept="image/*"
                                className="hidden"
                            />
                        </div>
                    </div>

                    {/* Nom de l'organisation */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Nom de l'organisation *
                        </label>
                        <input
                            type="text"
                            value={organization.name}
                            onChange={(e) => onChange('name', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            placeholder="Entrez le nom de l'organisation"
                        />
                    </div>

                    {/* Numéro de téléphone */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Numéro de téléphone *
                        </label>
                        <div className="relative">
                            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="tel"
                                value={organization.phoneNumber}
                                onChange={(e) => onChange('phoneNumber', e.target.value)}
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                placeholder="+243 XX XXX XXXX"
                            />
                        </div>
                    </div>

                    {/* Catégorie */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Catégorie *
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {categories.map(category => {
                                const Icon = category.icon;
                                return (
                                    <button
                                        key={category.id}
                                        type="button"
                                        onClick={() => onChange('category', category.id)}
                                        className={`p-4 rounded-2xl border-2 text-left transition-all ${
                                            organization.category === category.id
                                                ? `border-amber-500 bg-gradient-to-r ${category.color} bg-opacity-10`
                                                : 'border-gray-200 hover:border-amber-300'
                                        }`}
                                    >
                                        <div className="flex items-center space-x-3">
                                            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${
                                                organization.category === category.id
                                                    ? `bg-gradient-to-r ${category.color} text-white`
                                                    : 'bg-gray-100 text-gray-600'
                                            }`}>
                                                <Icon />
                                            </div>
                                            <span className={`font-medium ${
                                                organization.category === category.id
                                                    ? 'text-amber-700'
                                                    : 'text-gray-700'
                                            }`}>
                                                {category.label}
                                            </span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Logo (emoji) */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Logo (Emoji)
                        </label>
                        <div className="grid grid-cols-5 gap-3">
                            {logos.map(logo => (
                                <button
                                    key={logo}
                                    type="button"
                                    onClick={() => onChange('logo', logo)}
                                    className={`w-12 h-12 rounded-2xl text-xl flex items-center justify-center transition-all ${
                                        organization.logo === logo
                                            ? 'bg-amber-500 text-white shadow-lg scale-110'
                                            : 'bg-gray-100 hover:bg-gray-200'
                                    }`}
                                >
                                    {logo}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description
                        </label>
                        <textarea
                            value={organization.description}
                            onChange={(e) => onChange('description', e.target.value)}
                            rows="4"
                            className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            placeholder="Décrivez votre organisation..."
                        />
                    </div>
                </div>

                <div className="flex space-x-3 p-6 border-t border-gray-200">
                    <button
                        onClick={onClose}
                        className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-2xl hover:bg-gray-50 transition-colors"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={onSave}
                        disabled={!organization.name || !organization.phoneNumber}
                        className="flex-1 px-4 py-3 bg-gradient-to-r from-amber-500 to-yellow-500 text-white rounded-2xl font-semibold hover:from-amber-600 hover:to-yellow-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {title.includes('Créer') ? 'Créer' : 'Modifier'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OrganizationsPage;